import React from 'react';
import { DeckIcon } from '~/design-system/elements/DeckIcon/DeckIcon';
import { cn } from '~/lib/utils';
import styles from './ComposerButton.module.css';
import type { ChatTheme } from './theme';
import { chatThemeClass } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

/**
 * The composer's only control shape — one 32x32 round slot, three jobs.
 *
 * `add` on the left, `send`/`stop` on the right. Four separate extraction passes proposed this as
 * four components (`IconButton`, `ComposerIconButton`, `ComposerSendButton`,
 * `AddButton`/`SendButton`/`StopButton`); in the file they are one box that never changes size,
 * only its fill and its glyph. Keeping them one component is what guarantees the toolbar's
 * trailing edge does not shift when a send becomes a stop mid-generation.
 */
export type ComposerButtonProps = {
  onClick?: () => void;
  /**
   * Removes the pointer and the press/hover response. It deliberately does **not** dim: the
   * `Chat/Disabled` artboard dims the whole composer card at `opacity: 0.45` and leaves
   * every child painted exactly as it is at rest. See the note in the stylesheet.
   */
  disabled?: boolean;
  /** Accessible name — every action here is icon-only, so it is required. */
  label: string;
  theme?: ChatTheme;
  /** Stories and visual tests only: paints the hover state without a real pointer. */
  forceHover?: boolean;
  className?: string;
} & (
  | {
      /** The `+`. Ghost at rest; a `--cv15-fill-weak` wash under a brightened glyph on hover. */
      action: 'add';
      /**
       * The add menu is open beneath it (`Chat/Add menu`). Paints the identical wash to
       * hover, because the affordance is the same one held down — the pointer may have moved on
       * to the menu, but the button it came from is still the thing that is open.
       */
      open?: boolean;
    }
  | {
      /** The arrow. `active` is "there is something to send", not "focused". */
      action: 'send';
      /**
       * `Chat/Typing`, whose caption is just "send goes white": the slot stops being a
       * tinted hole and becomes the one solid object in the bar.
       */
      active: boolean;
    }
  | {
      /** The 15px square shown while the model is generating (`Chat/Generating`). */
      action: 'stop';
    }
);

/**
 * Glyph per action, verbatim from the artboards.
 *
 * The three sizes are not a ladder anyone can extend — they are the three numbers the design
 * draws, each chosen so the *ink* looks the same weight inside a 32px circle even though the
 * boxes differ: the plus is mostly air so it needs 21, the arrow is a solid stem at 19, and the
 * stop square is filled edge to edge so 15 is already the loudest of the three.
 */
const GLYPH = {
  add: { name: 'add', size: 21 },
  send: { name: 'arrow-up', size: 19 },
  // `stop-square.svg` is the one registry file already authored with `fill="currentColor"`, so it
  // must NOT be passed `preserveColors` — that would strand it at its authored colour.
  stop: { name: 'stop-square', size: 15 },
} as const;

export const ComposerButton = React.forwardRef<HTMLButtonElement, ComposerButtonProps>(
  function ComposerButton(props, ref) {
    const { action, onClick, disabled = false, label, theme: themeProp, forceHover = false, className } = props;
    const theme = useV15Theme(themeProp);

    // Narrowed off `props` rather than off the destructured `action`, so TypeScript keeps the
    // discriminated union honest without a cast.
    const open = props.action === 'add' ? (props.open ?? false) : false;
    const active = props.action === 'send' ? props.active : false;

    const glyph = GLYPH[action];

    return (
      <button
        ref={ref}
        type="button"
        className={cn(
          chatThemeClass(theme),
          styles.button,
          styles[action],
          action === 'send' && active && styles.sendActive,
          open && styles.open,
          forceHover && styles.forceHover,
          className,
        )}
        aria-label={label}
        title={label}
        // Only the add button owns a popover; send and stop commit immediately.
        aria-haspopup={action === 'add' ? 'menu' : undefined}
        aria-expanded={action === 'add' ? open : undefined}
        disabled={disabled}
        onClick={onClick}
      >
        <DeckIcon name={glyph.name} size={glyph.size} />
      </button>
    );
  },
);
